import React, { useState, useEffect, useMemo } from 'react';
import BaseWidget from './BaseWidget';

/**
 * Formula Builder Widget
 * Allows users to create custom formulas/equations for KPIs
 */
const FormulaBuilderWidget = ({ id, config = {}, onRemove, onConfigure, isEditing, theme = 'light' }) => {
  const [formulas, setFormulas] = useState(() => config.formulas || []);
  const [isBuilding, setIsBuilding] = useState(false);
  const [newFormulaName, setNewFormulaName] = useState('');
  const [newFormulaExpression, setNewFormulaExpression] = useState('');
  const [result, setResult] = useState(null);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);

  // Load Chart of Accounts from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('intelAgentChartOfAccounts');
      if (stored) {
        setChartOfAccounts(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading Chart of Accounts:', error);
    }
  }, []);

  // Create variables from Chart of Accounts and sample data
  const variables = useMemo(() => {
    const vars = {
      // Sample data
      revenue: 24500,
      expenses: 8200,
      clients: 50,
      appointments: 120
    };

    // Add Chart of Accounts as variables (use name as key, 0 as default value)
    chartOfAccounts.forEach(account => {
      if (account.name) {
        // Create a safe variable name from account name
        const varName = account.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
        vars[varName] = 0; // Default value, would need actual data in production
      }
    });

    return vars;
  }, [chartOfAccounts]);

  // Sample data for calculations (keep for backward compatibility)
  const sampleData = {
    revenue: 24500,
    expenses: 8200,
    clients: 50,
    appointments: 120
  };

  const operators = ['+', '-', '*', '/', '(', ')'];
  const functions = ['SUM', 'AVG', 'MAX', 'MIN'];

  const addToFormula = (value) => {
    setNewFormulaExpression(prev => prev + value);
  };

  const calculateFormula = () => {
    try {
      // Simple evaluation - replace variable names with values
      let expression = newFormulaExpression;
      Object.keys(variables).forEach(key => {
        expression = expression.replace(new RegExp(key, 'g'), variables[key]);
      });

      // Basic evaluation (math-only)
const result = safeEvalMath(expression);
setResult(result);
    } catch (error) {
      setResult('Error: Invalid formula');
    }
  };

  const saveFormula = () => {
    if (newFormulaName && newFormulaExpression) {
      const newFormula = {
        id: Date.now(),
        name: newFormulaName,
        expression: newFormulaExpression,
        result: result
      };
      setFormulas([...formulas, newFormula]);
      setNewFormulaName('');
      setNewFormulaExpression('');
      setResult(null);
      setIsBuilding(false);
    }
  };

  const deleteFormula = (id) => {
    setFormulas(formulas.filter(f => f.id !== id));
  };

  return (
    <BaseWidget
      id={id}
      title="Formula Builder"
      onRemove={onRemove}
      onConfigure={onConfigure}
      isRemovable={isEditing}
      isConfigurable={isEditing}
      theme={theme}
      headerActions={
        isEditing ? (
          <div className="widget-drag-handle px-2 py-1 cursor-move">
            <svg className="w-4 h-4 text-[var(--ink-muted)]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </div>
        ) : null
      }
    >
      <div className="p-4">
        {!isBuilding ? (
          <>
            {/* Saved Formulas */}
            <div className="space-y-3 mb-4">
              {formulas.length === 0 ? (
                <div className="text-center py-8 text-[var(--ink-muted)]">
                  No custom formulas yet. Click "New Formula" to create one.
                </div>
              ) : (
                formulas.map(formula => (
                  <div key={formula.id} className="p-3 rounded-lg bg-[var(--surface-sunken)] border border-[var(--surface-border)]">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-[var(--ink)]">{formula.name}</div>
                        <div className="text-xs text-[var(--ink-muted)] mt-1 font-mono">{formula.expression}</div>
                        {formula.result && (
                          <div className="text-lg font-bold text-[var(--accent-ink)] mt-2">
                            = {typeof formula.result === 'number' ? formula.result.toLocaleString() : formula.result}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => deleteFormula(formula.id)}
                        className="text-[var(--ui-alert)] hover:opacity-80"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <button
              onClick={() => setIsBuilding(true)}
              className="w-full px-4 py-2 text-sm font-medium text-[var(--btn-ink)] bg-[var(--btn-fill)] rounded-lg hover:bg-[var(--btn-fill-hover)] transition"
            >
              + New Formula
            </button>
          </>
        ) : (
          <>
            {/* Formula Builder */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[var(--ink)] mb-1">Formula Name</label>
                <input
                  type="text"
                  value={newFormulaName}
                  onChange={(e) => setNewFormulaName(e.target.value)}
                  placeholder="e.g., Net Profit Margin"
                  className="w-full px-3 py-2 text-sm rounded border border-[var(--surface-border)] bg-[var(--surface-raised)] text-[var(--ink)]"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[var(--ink)] mb-1">Expression</label>
                <div className="p-3 rounded border border-[var(--surface-border)] bg-[var(--surface-raised)] min-h-[60px] font-mono text-sm text-[var(--ink)]">
                  {newFormulaExpression || 'Build your formula...'}
                </div>
              </div>

              {/* Variables - Sample Data */}
              <div>
                <div className="text-xs font-medium text-[var(--ink)] mb-2">Sample Metrics</div>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(sampleData).map(key => (
                    <button
                      key={key}
                      onClick={() => addToFormula(key)}
                      className="px-2 py-1 text-xs font-medium rounded bg-[var(--nav-active-bg)] text-[var(--nav-active-ink)] hover:brightness-95"
                    >
                      {key}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chart of Accounts - Scrollable List */}
              {chartOfAccounts.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-[var(--ink)] mb-2">Chart of Accounts</div>
                  <div className="max-h-32 overflow-y-auto border border-[var(--surface-border)] rounded bg-[var(--surface-sunken)] p-2">
                    <div className="space-y-1">
                      {chartOfAccounts.map((account, index) => {
                        const varName = account.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
                        return (
                          <button
                            key={index}
                            onClick={() => addToFormula(varName)}
                            className="w-full text-left px-2 py-1 text-xs rounded bg-[var(--surface-raised)] text-[var(--ink)] hover:bg-[var(--surface-border)] border border-[var(--surface-border)] transition"
                            title={`Click to add: ${varName}`}
                          >
                            <span className="font-medium text-[var(--ui-positive)]">{account.name}</span>
                            {account.type && (
                              <span className="ml-2 text-xs text-[var(--ink-muted)]">
                                ({account.type})
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="text-xs text-[var(--ink-muted)] mt-1">
                    Click any account to add it to your formula
                  </div>
                </div>
              )}

              {/* Operators */}
              <div>
                <div className="text-xs font-medium text-[var(--ink)] mb-2">Operators</div>
                <div className="flex flex-wrap gap-2">
                  {operators.map(op => (
                    <button
                      key={op}
                      onClick={() => addToFormula(op)}
                      className="px-3 py-1 text-xs font-medium rounded bg-[var(--surface-border)] text-[var(--ink)] hover:bg-[var(--surface-sunken)]"
                    >
                      {op}
                    </button>
                  ))}
                </div>
              </div>

              {/* Result */}
              {result !== null && (
                <div className="p-3 rounded-lg bg-[var(--ui-positive)]/10 border border-[var(--ui-positive)]/30">
                  <div className="text-xs text-[var(--ui-positive)] font-medium">Result:</div>
                  <div className="text-2xl font-bold text-[var(--ui-positive)] mt-1">
                    {typeof result === 'number' ? result.toLocaleString() : result}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={calculateFormula}
                  className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] transition"
                >
                  Calculate
                </button>
                <button
                  onClick={() => setNewFormulaExpression('')}
                  className="px-4 py-2 text-sm font-medium text-[var(--ink)] bg-[var(--surface-border)] rounded-lg hover:bg-[var(--surface-sunken)] transition"
                >
                  Clear
                </button>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={saveFormula}
                  disabled={!newFormulaName || !newFormulaExpression}
                  className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] disabled:bg-[var(--surface-border)] disabled:text-[var(--ink-muted)] disabled:cursor-not-allowed transition"
                >
                  Save Formula
                </button>
                <button
                  onClick={() => {
                    setIsBuilding(false);
                    setNewFormulaName('');
                    setNewFormulaExpression('');
                    setResult(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-[var(--ink)] bg-[var(--surface-raised)] border border-[var(--surface-border)] rounded-lg hover:bg-[var(--surface-sunken)] transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </BaseWidget>
  );
};

function safeEvalMath(input) {
  const expr = String(input ?? "").trim();

  if (!/^[0-9+\-*/().\s]+$/.test(expr)) {
    throw new Error("Invalid characters in formula");
  }

  if (expr.length === 0) {
    throw new Error("Empty formula");
  }

  // eslint-disable-next-line no-new-func
  return Function(`"use strict"; return (${expr});`)();
}

export default FormulaBuilderWidget;
